import { LicenseInfo } from '@mui/x-license';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { HashRouter, Route, Switch } from 'react-router-dom';

import { AuthExpirationProvider, AuthManager, ThemeProvider, ToastProvider, api } from 'common';

import { ErrorBoundary } from './ErrorBoundry.tsx';
import { PageTitle } from './PageTitle.tsx';
import { BrowserNotificationsProvider } from './components/BrowserNotifications/useBrowserNotifications.ts';
import { useBrowserNotificationsPoller } from './components/BrowserNotifications/useBrowserNotificationsPoller.ts';
import { StaffMarkdownProvider } from './components/Markdown/Provider.tsx';
import { NavigationDrawer, SideNavigationModeProvider } from './components/Navigation';
import { AuthWarningBanner } from './components/Navigation/AuthWarningBanner.tsx';
import { NavigationLayout } from './components/Navigation/Layout.tsx';
import { PrivateRoute } from './components/PrivateRoute';
import { AppRoutes } from './scenes/AppRoutes';
import { CopilotRoutes } from './scenes/CopilotRoutes.tsx';
import { Login } from './scenes/Login';
import { LoginSuccess } from './scenes/LoginSuccess';
import { TermsOfUse } from './scenes/TermsOfUse';
/**
 * This is the single place where we allow PostHogProvider
 */
// eslint-disable-next-line no-restricted-imports
import { PostHogProvider } from './utils/events/Provider.tsx';
/**
 * This is the single place where we allow usePostHogIdentifyEffect
 */
// eslint-disable-next-line no-restricted-imports
import { usePostHogIdentifyEffect } from './utils/events/useIdentifyEffect.ts';
/**
 * This is the single place where we allow LaunchDarklyProvider
 */
// eslint-disable-next-line no-restricted-imports
import { LaunchDarklyProvider } from './utils/flags/Provider.tsx';
/**
 * This is the single place where we allow useLaunchDarklyIdentifyEffect
 */
// eslint-disable-next-line no-restricted-imports
import { useLaunchDarklyIdentifyEffect } from './utils/flags/useIdentifyEffect.ts';

const MUI_PUBLIC_LICENSE_KEY =
  'c7521b4ba5cacc087ff5247b93b71a68Tz0xMjY4NTksRT0xODA0ODk1OTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1pbml0aWFsLEtWPTI=';

LicenseInfo.setLicenseKey(MUI_PUBLIC_LICENSE_KEY);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry(failureCount, error) {
        if (error instanceof api.APIError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

export function App() {
  return (
    <PostHogProvider>
      <LaunchDarklyProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <DndProvider backend={HTML5Backend}>
              <ToastProvider>
                <AuthManager>
                  <HashRouter>
                    <PageTitle>
                      <StaffMarkdownProvider>
                        <BrowserNotificationsProvider>
                          <Switch>
                            <Route path="/login" component={Login} />
                            <Route path="/login-success" component={LoginSuccess} />
                            <Route path="/terms-of-use" component={TermsOfUse} />
                            <PrivateRoute path="/" component={ProtectedApp} />
                          </Switch>
                        </BrowserNotificationsProvider>
                      </StaffMarkdownProvider>
                    </PageTitle>
                  </HashRouter>
                </AuthManager>
              </ToastProvider>
            </DndProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </LaunchDarklyProvider>
    </PostHogProvider>
  );
}

function ProtectedApp() {
  useLaunchDarklyIdentifyEffect();
  usePostHogIdentifyEffect();
  return (
    <SideNavigationModeProvider>
      <AuthExpirationProvider>
        <ProtectedAppContent />
      </AuthExpirationProvider>
    </SideNavigationModeProvider>
  );
}

function ProtectedAppContent() {
  useBrowserNotificationsPoller();
  return (
    <Switch>
      <Route path="/copilot">
        <CopilotRoutes />
      </Route>
      <Route path="/">
        <NavigationLayout sidebar={<NavigationDrawer />}>
          <AuthWarningBanner />
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </NavigationLayout>
      </Route>
    </Switch>
  );
}
